import type {
	DragEndEvent,
	SensorDescriptor,
	SensorOptions,
} from '@dnd-kit/core';
import { Box, Flex, useMantineTheme } from '@mantine/core';
import type { EmbeddedPromptParentOut } from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { keys } from 'lodash-es';
import { memo, useCallback, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { SmallLoadingSpinner } from '../../../components/LoadingSpinner';
import { ChatHistoryItem } from './ChatHistoryItem';
import { ReorderableChatHistoryGroup } from './ReorderableChatHistoryGroup';

type ChatHistoryRow =
	| { type: 'header'; period: string }
	| { type: 'chat'; chat: EmbeddedPromptParentOut; isPinned: boolean };

export const AiChatHistory = memo(
	({
		groupedChats,
		pinnedChats,
		onSelectChat,
		onDeleteChat,
		editingChatId,
		editingChatTitle,
		onStartEditingChatTitle,
		onStopEditingChatTitle,
		onSaveEditingChatTitle,
		onModifyEditingChatTitle,
		onTogglePin,
		onPinnedChatDragEnd,
		activeChatId,
		sensors,
		onLoadMore,
		hasMore,
		isLoadingMore,
	}: {
		groupedChats: { [key: string]: EmbeddedPromptParentOut[] };
		pinnedChats: EmbeddedPromptParentOut[];
		onSelectChat: (chat: EmbeddedPromptParentOut) => void;
		onDeleteChat: (chat: EmbeddedPromptParentOut) => void;
		editingChatId: string | null;
		editingChatTitle: string;
		onStartEditingChatTitle: (chatId: string, currentTitle: string) => void;
		onStopEditingChatTitle: () => void;
		onSaveEditingChatTitle: (chatId: string) => void;
		onModifyEditingChatTitle: (title: string) => void;
		onTogglePin: (chatId: string) => void;
		onPinnedChatDragEnd: (event: DragEndEvent) => void;
		activeChatId: string;
		sensors: SensorDescriptor<SensorOptions>[];
		onLoadMore: () => void;
		hasMore: boolean;
		isLoadingMore: boolean;
	}) => {
		const theme = useMantineTheme();
		const rows: ChatHistoryRow[] = useMemo(() => {
			const out: ChatHistoryRow[] = [];

			const periods = keys(groupedChats);
			for (const period of periods) {
				out.push({ type: 'header', period });
				for (const chat of groupedChats[period]) {
					out.push({ type: 'chat', chat, isPinned: false });
				}
			}
			return out;
		}, [groupedChats]);

		const renderItem = useCallback(
			(index: number, row: ChatHistoryRow) => {
				if (row.type === 'header') {
					const isFirstPeriod = index === 0;
					const hasPinnedChats = pinnedChats.length > 0;
					const shouldAddMargin = !isFirstPeriod || hasPinnedChats;
					return (
						<Box key={`header-${row.period}`} mt={shouldAddMargin ? 'md' : 0}>
							<Text
								size="xs"
								color="text/secondary/default"
								px="xs"
								py="3xs"
								fw={600}
							>
								{row.period}
							</Text>
						</Box>
					);
				}

				const { chat, isPinned } = row;

				return (
					<ChatHistoryItem
						key={chat.id}
						chat={chat}
						isActive={chat.id === activeChatId}
						isPinned={isPinned}
						onSelectChat={onSelectChat}
						onDeleteChat={onDeleteChat}
						editingChatId={editingChatId}
						editingChatTitle={editingChatTitle}
						onStartEditingChatTitle={onStartEditingChatTitle}
						onStopEditingChatTitle={onStopEditingChatTitle}
						onSaveEditingChatTitle={onSaveEditingChatTitle}
						onModifyEditingChatTitle={onModifyEditingChatTitle}
						onTogglePin={onTogglePin}
					/>
				);
			},
			[
				activeChatId,
				pinnedChats.length,
				onSelectChat,
				onDeleteChat,
				editingChatId,
				editingChatTitle,
				onStartEditingChatTitle,
				onStopEditingChatTitle,
				onSaveEditingChatTitle,
				onModifyEditingChatTitle,
				onTogglePin,
			]
		);

		const renderFooter = useCallback(() => {
			if (!hasMore) {
				return null;
			}
			return (
				<Flex h={theme.other.space[10]} align="center" justify="center">
					{isLoadingMore ? <SmallLoadingSpinner /> : null}
				</Flex>
			);
		}, [hasMore, isLoadingMore]);

		const renderHeader = useCallback(() => {
			if (pinnedChats.length === 0) {
				return null;
			}
			return (
				<ReorderableChatHistoryGroup
					chats={pinnedChats}
					onSelectChat={onSelectChat}
					onDeleteChat={onDeleteChat}
					groupName="Pinned"
					groupIconName="pinned"
					editingChatId={editingChatId}
					editingChatTitle={editingChatTitle}
					onStartEditingChatTitle={onStartEditingChatTitle}
					onStopEditingChatTitle={onStopEditingChatTitle}
					onSaveEditingChatTitle={onSaveEditingChatTitle}
					onModifyEditingChatTitle={onModifyEditingChatTitle}
					onDragEnd={onPinnedChatDragEnd}
					onTogglePin={onTogglePin}
					activeChatId={activeChatId}
					sensors={sensors}
				/>
			);
		}, [
			pinnedChats,
			onSelectChat,
			onDeleteChat,
			editingChatId,
			editingChatTitle,
			onStartEditingChatTitle,
			onStopEditingChatTitle,
			onSaveEditingChatTitle,
			onModifyEditingChatTitle,
			onPinnedChatDragEnd,
			onTogglePin,
			activeChatId,
			sensors,
		]);

		return (
			<Box h="100%" pl="sm" mr="sm">
				<Virtuoso
					data={rows}
					itemContent={renderItem}
					endReached={() => {
						if (hasMore && !isLoadingMore) {
							onLoadMore();
						}
					}}
					computeItemKey={(index, item) =>
						item.type === 'header' ? `header-${item.period}` : item.chat.id
					}
					components={{
						Header: renderHeader,
						Footer: renderFooter,
					}}
				/>
			</Box>
		);
	}
);
